/**
 * Custom hook for article file search using Elasticsearch
 * 
 * Supports filtering, pagination, and full-text search
 */
import { useQuery } from '@tanstack/react-query'
import {
  searchArticleFiles,
  type ArticleFileSearchFilters,
  type ArticleFileSearchResponse,
} from '../api/articleFileSearchApi'
import type { FileInfo } from '../../../utils/types'

/**
 * Hook for searching article files with Elasticsearch
 * 
 * @param filters - Search filters
 * @param options - React Query options
 * @returns Query result with article file data
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error } = useArticleFileSearch({
 *   q: 'document',
 *   size: 20,
 *   articleId: 123
 * })
 * 
 * // With pagination:
 * const [page, setPage] = useState(0)
 * const { data } = useArticleFileSearch({
 *   q: searchTerm,
 *   size: 20,
 *   from: page * 20
 * })
 * ```
 */
export function useArticleFileSearch(
  filters: ArticleFileSearchFilters | null,
  options: {
    enabled?: boolean
    staleTime?: number
  } = {}
) {
  const safeFilters: ArticleFileSearchFilters = filters ?? { q: '' }
  const { enabled = true, staleTime = 30000 } = options

  const query = useQuery<ArticleFileSearchResponse, Error>({
    queryKey: ['article-file-search', safeFilters],
    queryFn: () => searchArticleFiles(safeFilters),
    enabled: enabled && !!safeFilters.q && safeFilters.q.trim().length > 0,
    staleTime,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    ...query,
    files: query.data?.results ?? [],
    total: query.data?.total ?? 0,
    maxScore: query.data?.max_score,
    hasMore: query.data?.page?.has_more ?? false,
    currentPage: Math.floor((safeFilters.from ?? 0) / (safeFilters.size ?? 20)),
  }
}

// Re-export types
export type { ArticleFileSearchFilters, ArticleFileSearchResponse }
